/**
 * IssuesFacetSidebar — Facetten (Issue-Typ, Schweregrad) als
 * aria-pressed Toggle-Buttons in role="group" (kein tablist).
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface IssuesFacetSidebarProps {
  typeCounts: Record<string, number>
  severityCounts: Record<string, number>
  activeTypes: Set<string>
  activeSeverities: Set<string>
  onToggleType: (v: string) => void
  onToggleSeverity: (v: string) => void
}

interface FacetGroupProps {
  label: string
  prefix: string
  counts: Record<string, number>
  active: Set<string>
  onToggle: (v: string) => void
  labelFor: (k: string) => string
}

function FacetGroup({ label, prefix, counts, active, onToggle, labelFor }: FacetGroupProps) {
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase text-neutral-400">{label}</span>
      {Object.entries(counts).map(([k, n]) => {
        const on = active.has(k)
        return (
          <button
            key={k}
            type="button"
            data-testid={`${prefix}-${k}`}
            aria-pressed={on}
            onClick={() => onToggle(k)}
            className={cn(
              'flex items-center justify-between rounded-lg px-3 py-1.5 text-sm transition-colors',
              on
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
            )}
          >
            <span>{labelFor(k)}</span>
            <span className="text-xs text-neutral-400">{n}</span>
          </button>
        )
      })}
    </div>
  )
}

export function IssuesFacetSidebar(props: IssuesFacetSidebarProps) {
  const { t } = useTranslation()
  const sevLabel = (k: string) =>
    t(`productsIssues.workList.severity${k.charAt(0).toUpperCase()}${k.slice(1)}`, k)

  return (
    <aside className="flex w-56 flex-shrink-0 flex-col gap-4">
      <FacetGroup
        label={t('productsIssues.workList.facetType')}
        prefix="facet-type"
        counts={props.typeCounts}
        active={props.activeTypes}
        onToggle={props.onToggleType}
        labelFor={(k) => k}
      />
      <FacetGroup
        label={t('productsIssues.workList.facetSeverity')}
        prefix="facet-severity"
        counts={props.severityCounts}
        active={props.activeSeverities}
        onToggle={props.onToggleSeverity}
        labelFor={sevLabel}
      />
    </aside>
  )
}
